import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, HandHeart, ArrowRight, HourglassMedium, WarningCircle } from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";
import Seo from "../components/Seo";

const DonateSuccess = () => {
  const [searchParams] = useSearchParams();
  // PayPal return URL may include ?token=ORDER_ID&PayerID=... (one-time)
  // or ?subscription_id=... (subscription)
  const orderId = searchParams.get("token");
  const subscriptionId = searchParams.get("subscription_id");
  const donationId = searchParams.get("donation_id");

  const [state, setState] = useState({ status: orderId || subscriptionId ? "checking" : "info", data: null, message: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (orderId) {
          const res = await axios.post(`${API_BASE}/donations/orders/${orderId}/capture`, null, { withCredentials: false });
          if (active) setState({ status: "paid", data: res.data?.donation, message: "" });
        } else if (subscriptionId && donationId) {
          const res = await axios.post(
            `${API_BASE}/donations/subscriptions/${subscriptionId}/confirm`,
            { donation_id: donationId },
            { withCredentials: false }
          );
          if (active) setState({ status: "paid", data: res.data?.donation, message: "" });
        }
      } catch (err) {
        if (!active) return;
        const detail = err?.response?.data?.detail || "Couldn't confirm your donation.";
        setState({ status: "error", data: null, message: detail });
      }
    })();
    return () => {
      active = false;
    };
  }, [orderId, subscriptionId, donationId]);

  return (
    <div data-testid="page-donate-success" className="py-24 sm:py-32">
      <Seo title="Donation Received — Thank You" noindex />
      <div className="container-x max-w-2xl text-center">
        {state.status === "checking" && (
          <>
            <HourglassMedium size={56} weight="duotone" className="text-primary mx-auto animate-pulse" />
            <h1 className="mt-6 font-heading text-4xl font-medium text-ink-900">Confirming your gift…</h1>
            <p className="mt-4 text-ink-700">Just a moment while we record your donation.</p>
          </>
        )}

        {state.status === "info" && (
          <>
            <CheckCircle size={56} weight="duotone" className="text-primary mx-auto" />
            <h1 className="mt-6 font-heading text-4xl font-medium text-ink-900">Donation complete</h1>
            <p className="mt-4 text-ink-700">
              Thank you for partnering with the ministry. If you have any questions
              about your gift, please reach us at <strong>office@balministries.org</strong>.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark transition-all"
              data-testid="success-home-btn"
            >
              Return Home
              <ArrowRight size={18} weight="bold" />
            </Link>
          </>
        )}

        {state.status === "paid" && (
          <>
            <CheckCircle size={64} weight="duotone" className="text-primary mx-auto" />
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-medium text-ink-900">
              Thank you for your gift.
            </h1>
            <p className="mt-5 text-ink-700 text-lg leading-relaxed">
              Your{" "}
              {state.data?.amount && (
                <span className="font-semibold text-primary">
                  ${Number(state.data.amount).toFixed(2)}
                </span>
              )}{" "}
              {state.data?.mode === "subscription" ? "monthly gift" : "donation"} toward{" "}
              <strong>{state.data?.category_label}</strong> directly supports grief
              outreach, chaplain-led care, and free resources for hurting people.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/"
                data-testid="success-home-btn"
                className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark transition-all"
              >
                Return Home
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/request-support"
                data-testid="success-prayer-btn"
                className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-all"
              >
                <HandHeart size={18} weight="duotone" />
                Request Prayer Too
              </Link>
            </div>
          </>
        )}

        {state.status === "error" && (
          <>
            <WarningCircle size={56} weight="duotone" className="text-ink-500 mx-auto" />
            <h1 className="mt-6 font-heading text-4xl font-medium text-ink-900">We hit a snag.</h1>
            <p className="mt-4 text-ink-700">{state.message}</p>
            <Link
              to="/donate"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark"
              data-testid="error-retry-btn"
            >
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default DonateSuccess;
